import {
  type DefaultError,
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export function useInfiniteScroll<
  TPage,
  TItem,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  options: UseInfiniteQueryOptions<TPage, DefaultError, InfiniteData<TPage>, TQueryKey, TPageParam>,
  getItems: (page: TPage) => TItem[],
  { pauseOnNextPageError = false }: { pauseOnNextPageError?: boolean } = {},
) {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setLoadMoreTarget = useCallback((target: HTMLDivElement | null) => {
    const previousTarget = loadMoreRef.current;
    if (previousTarget === target) return;

    if (previousTarget) observerRef.current?.unobserve(previousTarget);
    loadMoreRef.current = target;
    if (target) observerRef.current?.observe(target);
  }, []);

  // Keep getItems ref-stable so the memo only re-runs when pages change
  const getItemsRef = useRef(getItems);
  getItemsRef.current = getItems;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
    ...rest
  } = useInfiniteQuery(options);

  const items = useMemo(
    () => data?.pages.flatMap((page) => getItemsRef.current(page)) ?? [],
    [data?.pages],
  );

  useEffect(() => {
    let isActive = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          isActive &&
          entries[0].isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !(pauseOnNextPageError && isFetchNextPageError)
        ) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observerRef.current = observer;

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      isActive = false;
      observer.disconnect();
      if (observerRef.current === observer) observerRef.current = null;
    };
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage, pauseOnNextPageError]);

  return {
    items,
    isLoading,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
    loadMoreRef,
    setLoadMoreTarget,
    data,
    ...rest,
  };
}
